import axios from 'axios'
import type { AxiosError } from 'axios'
import React from 'react'

import { useScript } from '@helpers/hooks'

import * as segmentUtils from './segmentUtils'
import type { UserConsent, WindowWithAnalyticsAndCookieFirst } from './types'
import * as cookies from './utils'

interface Props {
  writeKey: string
  cookieFirstSrc: string
  segmentSnippet: string
  hideRejectButton?: boolean
  /*
  This is a handler for `cf_init` event.
  `cf_init` event fired every time user refreshes the page
   */
  handleExistingUserConsent: (allowedCookies: string[]) => void
  /*
  This is a handler for `cf_services_consent` event
  This event is fired only once when user accepts/rejects consent on the first interaction with CMP
   */
  handleInitialUserConsent: (allowedCookies: string[]) => void
  handleSegmentError: (error: AxiosError | Error) => void
}

const HIDE_REJECT_BUTTON_STYLE = `
#cookiefirst-root div:has(> button[data-cookiefirst-action="reject"]) {
  display: none;
  width: 0px;
}
#cookiefirst-root div:has(> button[data-cookiefirst-action="adjust"]) {
  max-width: 100%;
  flex: 0 0 100%;
}
`

const CookieConsentManager = (props: Props) => {
  const {
    writeKey,
    handleInitialUserConsent: handleInitialUserConsentProps,
    handleExistingUserConsent: handleExistingUserConsentProps,
    handleSegmentError,
  } = props
  const handleInitialUserConsent = React.useCallback(
    async (e: Event | CustomEvent<Record<string, boolean>>) => {
      const allowedCookies = cookies.getAllowedCookies('detail' in e ? e.detail : {})
      handleInitialUserConsentProps(allowedCookies)
      try {
        if (writeKey !== 'off') {
          await segmentUtils.loadSegmentIntegrations('detail' in e ? e.detail : {}, writeKey)
        }
      } catch (error) {
        if (axios.isAxiosError(error) || error instanceof Error) {
          handleSegmentError(error)
        }
      }
    },
    [handleInitialUserConsentProps, writeKey, handleSegmentError]
  )

  // This function is executed  whenever user refreshes the page and already gave a consent for specific scripts
  const handleExistingUserConsent = React.useCallback(
    async (eventDetail?: UserConsent) => {
      const wd = window as WindowWithAnalyticsAndCookieFirst
      const hasConsented = wd.CookieFirst?.hasConsented ?? false

      if (hasConsented || eventDetail) {
        const userConsent = eventDetail ?? wd.CookieFirst?.acceptedServices ?? {}
        const allowedCookies = cookies.getAllowedCookies(userConsent)
        handleExistingUserConsentProps(allowedCookies)
        try {
          if (writeKey !== 'off') {
            await segmentUtils.loadSegmentIntegrations(userConsent, writeKey)
          }
        } catch (error) {
          if (axios.isAxiosError(error) || error instanceof Error) {
            handleSegmentError(error)
          }
        }
      }
    },
    [writeKey, handleExistingUserConsentProps, handleSegmentError]
  )

  useScript({ scriptSrc: props.cookieFirstSrc })
  useScript({ scriptContent: props.segmentSnippet })

  React.useEffect(() => {
    window.addEventListener('cf_init', () => {
      handleExistingUserConsent().catch(console.error)
    })
    window.addEventListener('cf_services_consent', (event: Event & { detail?: UserConsent }) => {
      handleExistingUserConsent(event.detail).catch(console.error)
    })
    return () => {
      window.removeEventListener('cf_init', () => {
        handleExistingUserConsent().catch(console.error)
      })
      window.removeEventListener('cf_services_consent', () => {
        handleExistingUserConsent().catch(console.error)
      })
    }
  }, [handleExistingUserConsent, handleInitialUserConsent])

  if (props.writeKey === 'off') {
    return null
  }

  if (props.hideRejectButton) {
    return (
      <style
        dangerouslySetInnerHTML={{
          __html: `${HIDE_REJECT_BUTTON_STYLE}`,
        }}
      />
    )
  }

  return null
}

export default CookieConsentManager
